import {
	Adjustments,
	ColorProfileName,
	Curve,
	LookTable,
	LookTableRGBGamma,
	LookTableRGBPrimaries,
	LookTableType,
} from "@/core/types"
import { mat3, vec2, vec3, vec4 } from "gl-matrix"
import {
	srgbToProphoto,
	prophotoToSrgb,
	applyTransform,
	xyzToWorkingSpace,
	workingSpaceToXyz,
	rgbToHsv,
	rgbToHue,
	displayP3ToProphoto,
	linearToGamma,
	gammaToLinear,
	multiplyMat3xVec3,
	hsvToRgb,
	lutPrimariesEncode,
	lutPrimariesDecode,
	lutGammaEncode,
	lutGammaDecode,
} from "./color-space"
import { colorWheelToRGBY, XYtoXYZ } from "@/core/utils/color-conversion"
import { whiteBalanceToXY } from "@/core/utils/temperature"
import {
	adjustPrimaries,
	getAdaptionMatrix,
	primaries,
	primariesToMatrix,
} from "@/core/utils/color"
import { clamp, multiplyMat3 } from "@/core/utils/math"
import { getDefaultAdjustments } from "@/core/adjustments"
import { getCurvesData } from "@/core/curves"
import { getHSLData } from "@/core/hsl"
import { getContrastCurve, getCurve, normalizePoint } from "@/core/tone-curve"
import { writeHSLPointColorData } from "@/core/hsl-point-color"
import { getParametricCurvesData } from "@/core/parametric-curves"

// +++ OPTIMIZATION: Create a pool of reusable temporary vectors.
// This is the key to avoiding memory allocation in the per-pixel rendering loop.
const _p_temp1: vec3 = [0, 0, 0]
const _p_temp2: vec3 = [0, 0, 0]
const _p_hsv: vec3 = [0, 0, 0]
const _p_gamma: vec3 = [0, 0, 0]
const _p_sub: vec3 = [0, 0, 0] // temp inside sub functions
const _p_sub_2: vec3 = [0, 0, 0] // temp inside sub functions

export interface AsShotWB {
	asShotTemp: number
	asShotTint: number
}

export enum ColorSpaceCpu {
	proPhoto = 0,
	sRGB = 1,
	gamma = 2,
	displayP3 = 3,
	toneMapInversed = 4,
}

export interface RenderOptions extends AsShotWB {
	inputColorSpace?: ColorSpaceCpu
	outColorSpace?: ColorSpaceCpu
	profileCurve: Curve | null
	colorLookTable: LookTable | null
	profileLookTable: LookTable | null
	userLut: LookTable | null
}

interface RenderData {
	exposureFactor: number
	workingSpaceMatrix: mat3
	whiteBalanceMatrix: mat3
	inputColorSpace: ColorSpaceCpu
	outColorSpace: ColorSpaceCpu
	parametricCurveData?: Float32Array
	whiteCurveData?: Float32Array
	redCurveData?: Float32Array
	greenCurveData?: Float32Array
	blueCurveData?: Float32Array
	hslData?: Float32Array
	contrastCurveData?: Float32Array
	colorGradingData?: ColorGradingParams
	pointColorsData?: HSLPointColor[]
	isColorProfileMonochrome?: boolean
	profileCurveData?: Float32Array
	profileCurveDataInv?: Float32Array
	colorLookTableDims?: vec3
	colorLookTableData?: Float32Array
	profileLookTableDims?: vec3
	profileLookTableData?: Float32Array
	userLutData?: Float32Array
	userLutDims?: vec3
	userLutType?: number
	userLutStrength?: number
	userLutGamma?: number
	userLutPrimaries?: number
}

export function renderPixel(
	out_color: vec3, // The final result is written here
	rgbPixel: vec3,
	params: Partial<Adjustments>,
	uniforms: RenderData,
): vec3 {
	const {
		temperature,
		tint,
		shadows = 0,
		highlights = 0,
		saturation = 0,
		vibrance = 0,
		whites = 0,
		blacks = 0,
		dehaze = 0,
	} = params

	// +++ OPTIMIZATION: Use a pointer-swapping technique with temp vectors.
	// `color` will always point to the vector holding the latest result.
	// `temp` will be used as the output for the next operation.
	// Many of the sub functions below can be totally in-place where directly using color as both input and output is fine.
	let color = vec3.copy(_p_temp1, rgbPixel)
	let temp = _p_temp2

	if (uniforms.inputColorSpace !== ColorSpaceCpu.toneMapInversed) {
		if (uniforms.inputColorSpace === ColorSpaceCpu.sRGB) {
			srgbToProphoto(color, color)
		} else if (uniforms.inputColorSpace === ColorSpaceCpu.displayP3) {
			displayP3ToProphoto(color, color)
		}

		const hue0 = rgbToHue(color)

		// Tone map inverse
		tonemapInv(color, color)
		setHue(color, color, hue0)
		maxVec3(color, color, 0.0)
	}

	linearToGamma(_p_gamma, color)
	const luma = getLuma(_p_gamma)
	const darkChannel = Math.min(_p_gamma[0], _p_gamma[1], _p_gamma[2])

	// White balance
	if (temperature != null && tint != null) {
		applyWhiteBalance(color, color, uniforms.whiteBalanceMatrix)
	}

	// Color calibration
	if (
		params.calibrationRedHue != null ||
		params.calibrationGreenHue != null ||
		params.calibrationBlueHue != null
	) {
		applyCalibration(temp, color, uniforms.workingSpaceMatrix)
		;[color, temp] = [temp, color]
	}

	// Exposure, highlights, shadows
	let hue = rgbToHue(color)

	if (uniforms.exposureFactor != 1) {
		applyExposureFactor(color, color, uniforms.exposureFactor)
	}

	if (shadows != 0 || highlights != 0) {
		applyShadowsHighlights(color, color, shadows ?? 0, highlights ?? 0, luma)
	}

	if (whites < 0 || blacks > 0) {
		contractBlacksWhites(color, color, whites, blacks)
	}

	clipVec3(color, color, 0.0, 1.0)
	setHue(color, color, hue)

	// Saturation
	if (!isColorProfileMonochrome(params)) {
		if (saturation != 0) {
			applySaturation(color, color, saturation)
		}

		if (vibrance != 0) {
			applyVibrance(color, color, vibrance)
		}
	}

	// Profile LUTs
	if (uniforms.colorLookTableData != undefined && uniforms.colorLookTableDims != undefined) {
		applyLookTable(color, color, uniforms.colorLookTableDims, uniforms.colorLookTableData)
	}

	if (uniforms.profileLookTableData != undefined && uniforms.profileLookTableDims != undefined) {
		applyLookTable(color, color, uniforms.profileLookTableDims, uniforms.profileLookTableData)
	}

	// Dehaze
	if (dehaze != 0) {
		hue = rgbToHue(color)
		linearToGamma(color, color)
		applyDehaze(temp, color, dehaze, darkChannel) // color is now output, temp is input
		;[color, temp] = [temp, color]
		gammaToLinear(color, color)
		setHue(color, color, hue) // Preserve hue in linear space not in gamma space for dehaze
	}

	// Tone mapping
	hue = rgbToHue(color)
	tonemap(color, color)
	setHue(color, color, hue)

	// Convert to gamma space
	linearToGamma(color, color)

	// profile tone curve
	if (uniforms.profileCurveData != undefined) {
		applyCurve(color, uniforms.profileCurveData, "white")
	}

	hue = rgbToHue(color)
	// expand whites and blacks
	if (whites > 0 || blacks < 0) {
		expandWhitesBlacks(color, color, whites, blacks)
	}
	// Contrast
	if (uniforms.contrastCurveData != undefined) {
		applyCurve(color, uniforms.contrastCurveData, "white")
	}
	setHue(color, color, hue)

	// back to linear space
	gammaToLinear(color, color)

	// User LUT (after gammaToLinear, before HSL — matches GPU pipeline in adjustments.frag)
	if (
		uniforms.userLutType === LookTableType.hsv &&
		uniforms.userLutData != undefined &&
		uniforms.userLutDims != undefined
	) {
		applyLookTable(temp, color, uniforms.userLutDims, uniforms.userLutData)
		const s = uniforms.userLutStrength ?? 1
		color[0] = mix(color[0], temp[0], s)
		color[1] = mix(color[1], temp[1], s)
		color[2] = mix(color[2], temp[2], s)
	} else if (
		uniforms.userLutType === LookTableType.rgb &&
		uniforms.userLutData != undefined &&
		uniforms.userLutDims != undefined
	) {
		applyLookTableRGB(
			color,
			color,
			uniforms.userLutDims,
			uniforms.userLutData,
			uniforms.userLutStrength ?? 1,
			uniforms.userLutGamma ?? LookTableRGBGamma.linear,
			uniforms.userLutPrimaries ?? LookTableRGBPrimaries.sRGB,
		)
		clipVec3(color, color, 0, 1)
	}

	// HSL
	if (uniforms.hslData != undefined) {
		applyHsl(temp, color, uniforms.hslData)
		;[color, temp] = [temp, color]
	}

	if (isColorProfileMonochrome(params)) {
		applySaturation(color, color, -1.0)
	}

	// Point colors
	if (!isColorProfileMonochrome(params)) {
		if (uniforms.pointColorsData != undefined) {
			applyGlobalPointColorHsl(color, color, uniforms.pointColorsData)
		}
	}

	// Color grading
	if (uniforms.colorGradingData != undefined) {
		applyColorGrading(color, color, uniforms.colorGradingData)
	}

	// Apply curves at last in place
	applyAllCurves(color, uniforms)

	// Handle output color space
	if (uniforms.outColorSpace === ColorSpaceCpu.sRGB) {
		prophotoToSrgb(out_color, color)
		return out_color
	}

	if (uniforms.outColorSpace === ColorSpaceCpu.gamma) {
		linearToGamma(out_color, color)
		return out_color
	}

	return vec3.copy(out_color, color) // prophoto
}

export function renderCpu(
	srgbImg: Float32Array,
	adjustments: Partial<Adjustments>,
	options: RenderOptions,
): Float32Array {
	const { asShotTemp, asShotTint } = options
	const { temperature, tint } = adjustments
	const asShotWhite = XYtoXYZ(whiteBalanceToXY([asShotTemp, asShotTint]))
	const adjustedWhite = XYtoXYZ(whiteBalanceToXY([temperature ?? asShotTemp, tint ?? asShotTint]))
	const whiteBalanceMatrixXyz = getAdaptionMatrix(adjustedWhite, asShotWhite)
	const wbTransformMatrix = multiplyMat3(
		multiplyMat3(xyzToWorkingSpace, whiteBalanceMatrixXyz),
		workingSpaceToXyz,
	)

	const calibratedPrimaries = adjustPrimaries(primaries.proPhoto, {
		redHue: adjustments.calibrationRedHue,
		redSat: adjustments.calibrationRedSat,
		greenHue: adjustments.calibrationGreenHue,
		greenSat: adjustments.calibrationGreenSat,
		blueHue: adjustments.calibrationBlueHue,
		blueSat: adjustments.calibrationBlueSat,
	})
	const workingSpaceMatrix = primariesToMatrix(calibratedPrimaries)

	const fullAdjustments = { ...getDefaultAdjustments(), ...adjustments }
	const curveData = getCurvesData(fullAdjustments)
	let parametricCurvesData: Float32Array | undefined
	if (
		adjustments.parametricHighlights ||
		adjustments.parametricLights ||
		adjustments.parametricDarks ||
		adjustments.parametricShadows
	) {
		parametricCurvesData = getParametricCurvesData(fullAdjustments)
	}

	const uniforms: RenderData = {
		exposureFactor: Math.pow(2, adjustments.exposure ?? 0),
		workingSpaceMatrix,
		whiteBalanceMatrix: wbTransformMatrix,
		inputColorSpace: options.inputColorSpace ?? ColorSpaceCpu.sRGB,
		outColorSpace: options.outColorSpace ?? ColorSpaceCpu.proPhoto,
		parametricCurveData: parametricCurvesData,
		whiteCurveData: isCurveApplied(adjustments.curvesAll)
			? curveData.filter((_, i) => i % 4 === 3)
			: undefined,
		redCurveData: isCurveApplied(adjustments.curvesRed)
			? curveData.filter((_, i) => i % 4 === 0)
			: undefined,
		greenCurveData: isCurveApplied(adjustments.curvesGreen)
			? curveData.filter((_, i) => i % 4 === 1)
			: undefined,
		blueCurveData: isCurveApplied(adjustments.curvesBlue)
			? curveData.filter((_, i) => i % 4 === 2)
			: undefined,
		contrastCurveData: adjustments.contrast
			? getContrastCurve(adjustments.contrast, 256)
			: undefined,
	}

	if (options.profileCurve != undefined) {
		const points = options.profileCurve.map(normalizePoint)
		uniforms.profileCurveData = getCurve(points, 256)
		// uniforms.profileCurveDataInv = getInverseCurve(points, 256)
	}
	if (options.colorLookTable != null) {
		uniforms.colorLookTableData = options.colorLookTable.data
		uniforms.colorLookTableDims = [
			options.colorLookTable.hueDivisions,
			options.colorLookTable.satDivisions,
			options.colorLookTable.valDivisions,
		]
	}
	if (options.profileLookTable != null) {
		uniforms.profileLookTableData = options.profileLookTable.data
		uniforms.profileLookTableDims = [
			options.profileLookTable.hueDivisions,
			options.profileLookTable.satDivisions,
			options.profileLookTable.valDivisions,
		]
	}
	if (options.userLut != null) {
		uniforms.userLutData = options.userLut.data
		uniforms.userLutDims = [
			options.userLut.hueDivisions,
			options.userLut.satDivisions,
			options.userLut.valDivisions,
		]
		uniforms.userLutType = options.userLut.type
		uniforms.userLutStrength = adjustments.userLutStrength ?? 1
		uniforms.userLutGamma = options.userLut.rgbGamma
		uniforms.userLutPrimaries = options.userLut.rgbPrimaries
	}

	if (
		adjustments.hueRed != null ||
		adjustments.hueGreen != null ||
		adjustments.hueBlue != null ||
		adjustments.hueAqua != null ||
		adjustments.hueOrange != null ||
		adjustments.hueYellow != null ||
		adjustments.huePurple != null ||
		adjustments.hueMagenta != null
	) {
		const hslData = getHSLData({ ...getDefaultAdjustments(), ...adjustments })
		uniforms.hslData = hslData
	}

	if (adjustments.gradingBlending != undefined && adjustments.gradingBalance != undefined) {
		uniforms.colorGradingData = getColorGradingData(adjustments)
	}

	if (adjustments.pointColors != undefined && adjustments.pointColors.length > 0) {
		uniforms.pointColorsData = getPointColorsData(adjustments)
	}

	return applyTransform(srgbImg, (out, pixel) => renderPixel(out, pixel, adjustments, uniforms))
}

function getColorGradingData(adjustments: Partial<Adjustments>) {
	const globalColor = colorWheelToRGBY([
		adjustments.gradingGlobalHue ?? 0,
		adjustments.gradingGlobalSaturation ?? 0,
		adjustments.gradingGlobalLuminance ?? 0,
	])

	const shadowsColor = colorWheelToRGBY([
		adjustments.gradingShadowsHue ?? 0,
		adjustments.gradingShadowsSaturation ?? 0,
		adjustments.gradingShadowsLuminance ?? 0,
	])

	const midtonesColor = colorWheelToRGBY([
		adjustments.gradingMidtonesHue ?? 0,
		adjustments.gradingMidtonesSaturation ?? 0,
		adjustments.gradingMidtonesLuminance ?? 0,
	])

	const highlightsColor = colorWheelToRGBY([
		adjustments.gradingHighlightsHue ?? 0,
		adjustments.gradingHighlightsSaturation ?? 0,
		adjustments.gradingHighlightsLuminance ?? 0,
	])

	return {
		globalColor,
		shadowsColor,
		midtonesColor,
		highlightsColor,
		gradingBlending: adjustments.gradingBlending ?? 0.5,
		gradingBalance: adjustments.gradingBalance ?? 0.0,
	}
}

function getPointColorsData(adjustments: Partial<Adjustments>) {
	if (adjustments.pointColors == undefined || adjustments.pointColors.length == 0) {
		return undefined
	}
	const globalBufferData = new Float32Array(adjustments.pointColors.length * 16)
	writeHSLPointColorData({ ...getDefaultAdjustments(), ...adjustments }, globalBufferData, 0)

	const pointColorsData: HSLPointColor[] = []
	for (let i = 0; i < globalBufferData.length; i += 16) {
		pointColorsData.push({
			shiftHue: globalBufferData[i] ?? 0,
			shiftSat: globalBufferData[i + 1] ?? 0,
			shiftLum: globalBufferData[i + 2] ?? 0,
			range: globalBufferData[i + 3] ?? 0,
			hueRange: [
				globalBufferData[i + 4] ?? 0,
				globalBufferData[i + 5] ?? 0,
				globalBufferData[i + 6] ?? 0,
				globalBufferData[i + 7] ?? 0,
			],
			lumRange: [
				globalBufferData[i + 8] ?? 0,
				globalBufferData[i + 9] ?? 0,
				globalBufferData[i + 10] ?? 0,
				globalBufferData[i + 11] ?? 0,
			],
			satRange: [
				globalBufferData[i + 12] ?? 0,
				globalBufferData[i + 13] ?? 0,
				globalBufferData[i + 14] ?? 0,
				globalBufferData[i + 15] ?? 0,
			],
		})
	}
	return pointColorsData
}

export function renderHSLAndColorGrading(
	prophotoImg: Float32Array,
	adjustments: Partial<Adjustments>,
	options: RenderOptions,
): Float32Array {
	const uniforms: Partial<RenderData> = {
		outColorSpace: options.outColorSpace ?? ColorSpaceCpu.proPhoto,
	}

	if (
		adjustments.hueRed != null ||
		adjustments.hueGreen != null ||
		adjustments.hueBlue != null ||
		adjustments.hueAqua != null ||
		adjustments.hueOrange != null ||
		adjustments.hueYellow != null ||
		adjustments.huePurple != null ||
		adjustments.hueMagenta != null
	) {
		const hslData = getHSLData({ ...getDefaultAdjustments(), ...adjustments })
		uniforms.hslData = hslData
	}

	if (adjustments.gradingBlending != null && adjustments.gradingBalance != null) {
		uniforms.colorGradingData = getColorGradingData(adjustments)
	}

	if (adjustments.pointColors != undefined && adjustments.pointColors.length > 0) {
		uniforms.pointColorsData = getPointColorsData(adjustments)
	}

	uniforms.isColorProfileMonochrome = isColorProfileMonochrome(adjustments)

	const fullAdjustments = { ...getDefaultAdjustments(), ...adjustments }
	const curveData = getCurvesData(fullAdjustments)
	let parametricCurvesData: Float32Array | undefined
	if (
		adjustments.parametricHighlights ||
		adjustments.parametricLights ||
		adjustments.parametricDarks ||
		adjustments.parametricShadows
	) {
		parametricCurvesData = getParametricCurvesData(fullAdjustments)
	}

	uniforms.parametricCurveData = parametricCurvesData
	uniforms.whiteCurveData = isCurveApplied(adjustments.curvesAll)
		? curveData.filter((_, i) => i % 4 === 3)
		: undefined
	uniforms.redCurveData = isCurveApplied(adjustments.curvesRed)
		? curveData.filter((_, i) => i % 4 === 0)
		: undefined
	uniforms.greenCurveData = isCurveApplied(adjustments.curvesGreen)
		? curveData.filter((_, i) => i % 4 === 1)
		: undefined
	uniforms.blueCurveData = isCurveApplied(adjustments.curvesBlue)
		? curveData.filter((_, i) => i % 4 === 2)
		: undefined

	return applyTransform(prophotoImg, (out, pixel) =>
		renderPixelForHSLAndColorGrading(out, pixel, uniforms),
	)
}

export function renderPixelForHSLAndColorGrading(
	out: vec3, // prophoto linear
	color: vec3, // prophoto linear
	uniforms: Partial<RenderData>,
): vec3 {
	const isMonochrome = uniforms.isColorProfileMonochrome ?? false

	let temp = _p_temp2

	// HSL
	if (uniforms.hslData != undefined) {
		applyHsl(temp, color, uniforms.hslData)
		;[color, temp] = [temp, color]
	}

	if (isMonochrome) {
		applySaturation(color, color, -1.0)
	}

	// Point colors
	if (!isMonochrome) {
		if (uniforms.pointColorsData != undefined) {
			applyGlobalPointColorHsl(color, color, uniforms.pointColorsData)
		}
	}

	// Color grading
	if (uniforms.colorGradingData != undefined) {
		applyColorGrading(color, color, uniforms.colorGradingData)
	}

	// Apply curves at last
	applyAllCurves(color, uniforms)

	// Handle output color space
	if (uniforms.outColorSpace === ColorSpaceCpu.sRGB) {
		return prophotoToSrgb(out, color)
	}

	if (uniforms.outColorSpace === ColorSpaceCpu.gamma) {
		return linearToGamma(out, color)
	}

	vec3.copy(out, color)
	return out // prophoto
}

export function srgbToToneMapInversed(out: vec3, color: vec3): vec3 {
	srgbToProphoto(color, color)

	// Tone map inverse
	const hue0 = rgbToHue(color)
	tonemapInv(color, color)
	setHue(out, color, hue0)
	maxVec3(out, out, 0.0)

	return out
}

export function applyWhiteBalance(out: vec3, color: vec3, wbTransformMatrix: mat3): vec3 {
	multiplyMat3xVec3(_p_sub, wbTransformMatrix, color)
	return maxVec3(out, _p_sub, 0)
}

export function applyExposure(out: vec3, color: vec3, exposure: number): vec3 {
	return multiplyVec3ByScalar(out, color, Math.pow(2, exposure))
}

export function applyExposureFactor(out: vec3, color: vec3, exposureFactor: number): vec3 {
	return multiplyVec3ByScalar(out, color, exposureFactor)
}

// Helper functions
function clipVec3(out: vec3, v: vec3, min: number, max: number): vec3 {
	out[0] = Math.min(Math.max(v[0], min), max)
	out[1] = Math.min(Math.max(v[1], min), max)
	out[2] = Math.min(Math.max(v[2], min), max)
	return out
}

function maxVec3(out: vec3, v: vec3, min: number): vec3 {
	out[0] = Math.max(v[0], min)
	out[1] = Math.max(v[1], min)
	out[2] = Math.max(v[2], min)
	return out
}

function multiplyVec3ByScalar(out: vec3, v: vec3, scalar: number): vec3 {
	out[0] = v[0] * scalar
	out[1] = v[1] * scalar
	out[2] = v[2] * scalar
	return out
}

function smoothstep(edge0: number, edge1: number, x: number): number {
	// Scale, bias and saturate x to 0..1 range
	x = Math.min(Math.max((x - edge0) / (edge1 - edge0), 0.0), 1.0)
	// Evaluate polynomial
	return x * x * (3 - 2 * x)
}

// Constants
const T_A = 1.2
const T_B = 0.0
const T_C = 0.96
const T_D = 0.22
const T_E = 0.02

export function tonemap(out: vec3, x: vec3): vec3 {
	// Clip input values
	const r = Math.min(Math.max(x[0], 0.0), 32.0)
	const g = Math.min(Math.max(x[1], 0.0), 32.0)
	const b = Math.min(Math.max(x[2], 0.0), 32.0)

	out[0] = (r * (T_A * r + T_B)) / (r * (T_C * r + T_D) + T_E)
	out[1] = (g * (T_A * g + T_B)) / (g * (T_C * g + T_D) + T_E)
	out[2] = (b * (T_A * b + T_B)) / (b * (T_C * b + T_D) + T_E)
	return out
}

export function tonemapInv(out: vec3, x: vec3): vec3 {
	const r = x[0]
	const A_r = r * T_C - T_A
	const B_r = r * T_D - T_B
	const C_r = r * T_E
	const discriminant_r = Math.max(B_r * B_r - 4.0 * A_r * C_r, 0)
	out[0] = (-B_r - Math.sqrt(discriminant_r)) / (2.0 * A_r)

	const g = x[1]
	const A_g = g * T_C - T_A
	const B_g = g * T_D - T_B
	const C_g = g * T_E
	const discriminant_g = Math.max(B_g * B_g - 4.0 * A_g * C_g, 0)
	out[1] = (-B_g - Math.sqrt(discriminant_g)) / (2.0 * A_g)

	const b = x[2]
	const A_b = b * T_C - T_A
	const B_b = b * T_D - T_B
	const C_b = b * T_E
	const discriminant_b = Math.max(B_b * B_b - 4.0 * A_b * C_b, 0)
	out[2] = (-B_b - Math.sqrt(discriminant_b)) / (2.0 * A_b)

	return out
}

export function setHue(out: vec3, rgb: vec3, hue: number): vec3 {
	const hsv = rgbToHsv(_p_hsv, rgb)
	hsv[0] = hue
	return hsvToRgb(out, hsv)
}

// ProPhoto RGB luminance coefficients
const PROPHOTO_LUMA: vec3 = [0.242655, 0.755158, 0.002187]

function getLuma(color: vec3): number {
	return color[0] * PROPHOTO_LUMA[0] + color[1] * PROPHOTO_LUMA[1] + color[2] * PROPHOTO_LUMA[2]
}

function applySaturation(out: vec3, color: vec3, saturation: number): vec3 {
	// Calculate luminance
	const luma = getLuma(color)

	// GLSL mix(x,y,a) = x * (1-a) + y * a
	// Mix between color and luma with amount = min(-saturation, 1.0)
	const mixAmount = Math.min(-saturation, 1.0)
	const invMixAmount = 1 - mixAmount

	out[0] = Math.max(color[0] * invMixAmount + luma * mixAmount, 0.0)
	out[1] = Math.max(color[1] * invMixAmount + luma * mixAmount, 0.0)
	out[2] = Math.max(color[2] * invMixAmount + luma * mixAmount, 0.0)
	return out
}

function applyCalibration(out: vec3, color: vec3, xyzToWorking: mat3): vec3 {
	// Convert to XYZ space
	const xyz = multiplyMat3xVec3(_p_sub, workingSpaceToXyz, color)

	// Convert back to working space
	const transformed = multiplyMat3xVec3(out, xyzToWorking, xyz)

	// GLSL mix(x,y,a) = x * (1-a) + y * a
	// With a = 2.0, this becomes: x * (-1) + y * 2
	out[0] = color[0] * -1 + transformed[0] * 2
	out[1] = color[1] * -1 + transformed[1] * 2
	out[2] = color[2] * -1 + transformed[2] * 2
	return out
}

function applyShadowsHighlights(
	out: vec3,
	color: vec3,
	shadows: number,
	highlights: number,
	lumaMap: number,
): vec3 {
	// Calculate mixed luminance
	const colorLuma = getLuma(color)
	const mixedLuma = Math.max(colorLuma + lumaMap, 0.0) * 0.5

	// Calculate masks with proper smoothstep
	const luma = smoothstep(0.0, 1.6, Math.sqrt(mixedLuma))
	const specularMask = Math.max(0.0, 1.0 - luma * luma)
	let highlightsMask = smoothstep(0.05, 1.0, lumaMap) * 1.2
	if (highlights > 0.0) {
		highlightsMask *= Math.max(0.0, 1.0 - colorLuma) * 1.1
	}
	const shadowsMask = 1.0 - smoothstep(0.0, 0.5, lumaMap)

	// Calculate exposure adjustment
	const exposureAmount = shadows * shadowsMask + highlights * highlightsMask * specularMask

	// Apply exposure
	return applyExposure(out, color, exposureAmount * 2.0)
}

function isCurveApplied(curve: Curve | undefined): boolean {
	if (curve == undefined) {
		return false
	}

	if (
		curve.length == 2 &&
		curve[0]?.[0] == 0 &&
		curve[0]?.[1] == 0 &&
		curve[1]?.[0] == 255 &&
		curve[1]?.[1] == 255
	) {
		return false
	}

	return true
}

function applyCurve(
	color: vec3,
	curveData: Float32Array,
	channel: "white" | "red" | "green" | "blue",
): vec3 {
	// Clip input color to [0, 1] range
	clipVec3(color, color, 0, 1)

	// Apply curve based on channel
	if (channel === "white") {
		color[0] = applyCurveToValue(color[0], curveData)
		color[1] = applyCurveToValue(color[1], curveData)
		color[2] = applyCurveToValue(color[2], curveData)
		return color
	} else {
		const channelMap = { red: 0, green: 1, blue: 2 }
		const channelIndex = channelMap[channel]
		color[channelIndex] = applyCurveToValue(color[channelIndex] as number, curveData)
		return color
	}
}

function applyCurveToValue(value: number, curveData: Float32Array): number {
	const curveSize = curveData.length

	let i = Math.floor(value * (curveSize - 1))
	i = clamp(i, 0, curveSize - 2)

	const p0 = curveData[i] as number
	const p1 = curveData[i + 1] as number
	const p0x = i / (curveSize - 1)
	const p1x = (i + 1) / (curveSize - 1)

	// Linear interpolation between points
	const t = (value - p0x) / (p1x - p0x)
	const result = p0 + t * (p1 - p0)

	return clamp(result, 0, 1)
}

function applyIncreasedSat(out: vec3, hsv: vec3, deltaSat: number): vec3 {
	const updatedSat = hsv[1] + deltaSat * 0.3

	// This is to make color transition smoother at large sat
	const baseSat = Math.max(hsv[1], 0.85)
	const sat = Math.max(updatedSat - baseSat, 0.0) * 0.5 + Math.min(updatedSat, baseSat)
	const clampedSat = clamp(sat, 0.0, 1.0)
	hsv[1] = clampedSat

	return hsvToRgb(out, hsv)
}

function applyDecreasedSat(out: vec3, originalColor: vec3, hsv: vec3, deltaSat: number): vec3 {
	const mx = Math.max(originalColor[0], Math.max(originalColor[1], originalColor[2]))
	const mn = Math.min(originalColor[0], Math.min(originalColor[1], originalColor[2]))
	const lightness = (mx + mn) / 2.0

	const color = hsvToRgb(_p_sub, hsv)

	// mix(vec3(lightness), color, 1.0 + deltaSat)
	const a = 1.0 + deltaSat
	out[0] = clamp(lightness * (1 - a) + color[0] * a, 0, 1)
	out[1] = clamp(lightness * (1 - a) + color[1] * a, 0, 1)
	out[2] = clamp(lightness * (1 - a) + color[2] * a, 0, 1)
	return out
}

function applyIncreasedLum(out: vec3, color: vec3, hsv: vec3, sat: number, deltaLum: number): vec3 {
	const lumDelta = deltaLum * 1.25

	// increase lum: darker area has stronger effect
	const valMap = 1.15 - smoothstep(0.0, 1.0, hsv[2]) * 0.7
	const satValInfluence = sat * valMap * 2.0

	// pow(color, vec3(1.0 - lumDelta * satValInfluence))
	color = color.map((c) => Math.pow(c, 1.0 - lumDelta * satValInfluence)) as vec3

	const newV = Math.max(color[0], Math.max(color[1], color[2]))
	const deltaSat =
		deltaLum * smoothstep(0.0, 1.0, hsv[2]) * 0.5 * (1.3 - smoothstep(0.0, 1.0, hsv[1]) * 0.4)

	hsv[1] = Math.max(hsv[1] - deltaSat, 0.2 * hsv[1])
	hsv[2] = newV
	return hsvToRgb(out, hsv)
}

function applyDecreasedLum(out: vec3, color: vec3, hsv: vec3, sat: number, deltaLum: number): vec3 {
	// negative lum slider nonlinear
	const lumDelta = deltaLum * Math.sqrt(Math.abs(deltaLum)) * 5.0

	// decrease lum: brighter area has stronger effect
	const valMap = 0.55 + smoothstep(0.25, 0.9, hsv[2]) * 1.3
	const satValInfluence = sat * valMap * 2.0

	// pow(color, vec3(1.0 - lumDelta * satValInfluence))
	color = color.map((c) => Math.pow(c, 1.0 - lumDelta * satValInfluence)) as vec3

	const newV = Math.max(color[0], Math.max(color[1], color[2]))
	const deltaV = newV - hsv[2]

	hsv[1] = Math.min(hsv[1] - 0.5 * deltaV, 1.0)
	hsv[2] = newV
	return hsvToRgb(out, hsv)
}

function applyHslDelta(out: vec3, color: vec3, hsv: vec3, delta: vec3): vec3 {
	const mx = Math.max(color[0], Math.max(color[1], color[2]))
	const mn = Math.min(color[0], Math.min(color[1], color[2]))
	const sat = mx - mn

	// Hue
	const hue = (hsv[0] + (delta[0] * 30.0) / 360.0 + 1.0) % 1.0
	hsv[0] = hue
	let result = _p_sub
	let temp = _p_sub_2
	hsvToRgb(result, hsv)

	// Luminance
	temp =
		delta[2] >= 0.0
			? applyIncreasedLum(temp, result, hsv, sat, delta[2])
			: applyDecreasedLum(temp, result, hsv, sat, delta[2])
	;[result, temp] = [temp, result]

	// Saturation
	rgbToHsv(hsv, result)
	temp =
		delta[1] >= 0.0
			? applyIncreasedSat(temp, hsv, delta[1])
			: applyDecreasedSat(temp, result, hsv, delta[1])

	return clipVec3(out, temp, 0, 1)
}

const hslDeltaVec3 = vec3.create()
function applyHsl(out: vec3, color: vec3, hslData: Float32Array): vec3 {
	const hsv = rgbToHsv(_p_hsv, color)

	const hueIndex = clamp(Math.round(hsv[0] * 360), 0, 359) * 3

	vec3.set(
		hslDeltaVec3,
		hslData[hueIndex] ?? 0,
		hslData[hueIndex + 1] ?? 0,
		hslData[hueIndex + 2] ?? 0,
	)
	vec3.scale(hslDeltaVec3, hslDeltaVec3, 4.0)
	vec3.subtract(hslDeltaVec3, hslDeltaVec3, [2, 2, 2])

	const satInfluence = smoothstep(0.0, 0.2, hsv[1])
	applyHslDelta(out, color, hsv, hslDeltaVec3)

	// mix(color, result, satInfluence)
	const invSatInfluence = 1 - satInfluence
	out[0] = color[0] * invSatInfluence + out[0] * satInfluence
	out[1] = color[1] * invSatInfluence + out[1] * satInfluence
	out[2] = color[2] * invSatInfluence + out[2] * satInfluence
	return out
}

// Helper functions to match GLSL
function saturate(x: number): number {
	return clamp(x, 0, 1)
}

export function falloff(x: number, m: number): number {
	x = x * m
	x = 1.0 / ((x + 1.0) * (x + 1.0))
	m = 1.0 / ((m + 1.0) * (m + 1.0))

	return (x - m) / (1.0 - m)
}

// Contract dynamic range
// Only apply for positive blacks and negative whites
export function contractBlacksWhites(out: vec3, color: vec3, whites: number, blacks: number): vec3 {
	blacks = Math.max(blacks, 0)
	whites = Math.min(whites, 0)

	const luma = getLuma(color)
	const x = saturate(Math.sqrt(luma))

	let whitesMask = smoothstep(0.05, 1, x)
	const specularMask = Math.max(0.0, 1.0 - luma * luma * luma * luma)
	whitesMask = whitesMask * specularMask

	const blacksMask = smoothstep(0.8, -0.4, x)

	whites = whites * whitesMask
	blacks = blacks * blacksMask

	const mult = Math.pow(2, whites) * Math.pow(2, blacks)
	out[0] = color[0] * mult
	out[1] = color[1] * mult
	out[2] = color[2] * mult
	return out
}

// Expand and clip dynamic range.
// Only apply for negative blacks and positive whites!
export function expandWhitesBlacks(out: vec3, color: vec3, whites: number, blacks: number): vec3 {
	blacks = Math.min(blacks, 0)
	whites = Math.max(whites, 0)

	const x = saturate(getLuma(color))
	const whitesMask = falloff(x, -0.7)
	const whitesMaskLow = smoothstep(0.0, 1.0, Math.sqrt(x))
	const blacksMask = falloff(1.0 - x, -0.9)
	const mask = whitesMask * blacksMask

	whites = whites * 0.33
	blacks = blacks * 0.33

	blacks = blacks * mask
	whites = 1.0 + whites * mask * whitesMaskLow

	out[0] = (color[0] * (1.0 - blacks) + blacks) * whites
	out[1] = (color[1] * (1.0 - blacks) + blacks) * whites
	out[2] = (color[2] * (1.0 - blacks) + blacks) * whites

	return clipVec3(out, out, 0, 1)
}

function selectHue(hue: number, point: number, range: number): number {
	// fract(x) is equivalent to x - Math.floor(x)
	const centered = Math.abs(hue - point - 0.5 - Math.floor(hue - point - 0.5) - 0.5)
	return smoothstep(1.0 - range, 1.0, 1.0 - centered * 2.0)
}

export function applyVibrance(out: vec3, color: vec3, vibrance: number): vec3 {
	const intensity = vibrance > 1.0 ? vibrance : vibrance * 0.6

	// sqrt(color) to convert to gamma space
	_p_gamma[0] = Math.sqrt(color[0])
	_p_gamma[1] = Math.sqrt(color[1])
	_p_gamma[2] = Math.sqrt(color[2])

	const hsv = rgbToHsv(_p_hsv, _p_gamma)
	const hue = hsv[0]
	const sat = hsv[1]
	const val = 1.0 - Math.abs(hsv[2] - 0.5)

	const redFactor = intensity > 1.0 ? 0.5 : 0.16

	// 0.085 is approximately 30 degrees in normalized [0,1] space
	const skinHue = selectHue(hue, 0.085, 0.5)

	const satMask = smoothstep(0.0, 1.0, val * sat)
	const redMask = 1.0 - skinHue * redFactor

	hsv[1] += intensity * Math.sqrt(satMask) * redMask

	// Clamp HSV values and convert back to RGB
	const clampedHsv: vec3 = [clamp(hsv[0], 0, 1), clamp(hsv[1], 0, 1), clamp(hsv[2], 0, 1)]

	const res = hsvToRgb(_p_sub, clampedHsv)

	// Square the result to convert back to linear space
	out[0] = res[0] * res[0]
	out[1] = res[1] * res[1]
	out[2] = res[2] * res[2]
	return out
}

export function applyDehaze(out: vec3, color: vec3, dehaze: number, darkChannel: number): vec3 {
	const t0 = 0.1
	let A = 1.0
	const transmission = Math.max(t0, 1.0 - darkChannel / A)
	const maxChannel = Math.max(color[0], Math.max(color[1], color[2]))

	// A = atmospheric light (haze color)
	// the lower the value, the brighter the dehaze result will be

	// Apply positive dehaze
	const dehazePositive = Math.max(dehaze, 0.0)
	let t = mix(1.0, transmission, Math.min(dehazePositive * 0.7, 1.0))
	const inv_t = 1.0 / t
	out[0] = Math.max((color[0] - A) * inv_t + A, 0.0)
	out[1] = Math.max((color[1] - A) * inv_t + A, 0.0)
	out[2] = Math.max((color[2] - A) * inv_t + A, 0.0)

	// Apply negative dehaze
	const dehazeNegative = Math.min(dehaze, 0.0)
	A = Math.min(1.05, maxChannel + 0.3 - 0.5 * dehazeNegative)
	t = mix(1.0, transmission, -4.0)
	const inv_t_haze = 1.0 / t
	_p_gamma[0] = Math.min(Math.max((out[0] - A) * inv_t_haze + A, 0.0), 1.0)
	_p_gamma[1] = Math.min(Math.max((out[1] - A) * inv_t_haze + A, 0.0), 1.0)
	_p_gamma[2] = Math.min(Math.max((out[2] - A) * inv_t_haze + A, 0.0), 1.0)

	// mix(result, haze, -dehazeNegative)
	const inv_mix = 1 + dehazeNegative
	out[0] = out[0] * inv_mix + _p_gamma[0] * -dehazeNegative
	out[1] = out[1] * inv_mix + _p_gamma[1] * -dehazeNegative
	out[2] = out[2] * inv_mix + _p_gamma[2] * -dehazeNegative
	return out
}

// Helper function to match GLSL mix
function mix(a: number, b: number, t: number): number {
	return a * (1 - t) + b * t
}

function mixVec3(out: vec3, a: vec3, b: vec3, t: number): vec3 {
	out[0] = mix(a[0], b[0], t)
	out[1] = mix(a[1], b[1], t)
	out[2] = mix(a[2], b[2], t)
	return out
}

interface ColorGradingParams {
	globalColor: vec4
	shadowsColor: vec4
	midtonesColor: vec4
	highlightsColor: vec4
	gradingBlending: number
	gradingBalance: number
}

const _global: vec3 = [0, 0, 0]
const _shadows: vec3 = [0, 0, 0]
const _highlights: vec3 = [0, 0, 0]
const _midtones: vec3 = [0, 0, 0]

export function applyColorGrading(
	out: vec3,
	color: vec3,
	{
		globalColor,
		shadowsColor,
		midtonesColor,
		highlightsColor,
		gradingBlending,
		gradingBalance,
	}: ColorGradingParams,
): vec3 {
	// Calculate luma using Rec. 601 coefficients
	const luma = color[0] * 0.299 + color[1] * 0.587 + color[2] * 0.114

	const maxChannel = Math.max(...color)
	const sat = maxChannel - Math.min(...color)
	const satMask = linearstep(1.0, 0.0, sat)

	// Global color adjustment
	_global[0] = mix(globalColor[0], 1.0, luma)
	_global[1] = mix(globalColor[1], 1.0, luma)
	_global[2] = mix(globalColor[2], 1.0, luma)

	const specularMask = 1.0 - luma * luma

	// Adjusted luma
	const adjustedLuma = 1.0 - Math.pow(1.0 - luma, Math.pow(2, gradingBalance * 4.0))
	const sqLuma = Math.sqrt(adjustedLuma)

	// Calculate masks
	const shadowsMask =
		mix(
			linearstep(0.15, 0.0, adjustedLuma),
			linearstep(1.0, 0.0, adjustedLuma),
			gradingBlending,
		) * specularMask

	const highlightsMask =
		mix(smoothstep(0.2, 1.0, adjustedLuma), linearstep(0.1, 1.0, sqLuma), gradingBlending) *
		specularMask

	const midtonesMask =
		mix(
			smoothstep(0.7, 0.0, Math.abs(sqLuma * 2.0 - 0.9)),
			linearstep(1.0, 0.0, Math.abs(sqLuma * 2.0 - 0.9)),
			gradingBlending,
		) *
		specularMask *
		(satMask * 0.4 + 0.6)

	// Apply color adjustments
	_shadows[0] = mix(1.0, shadowsColor[0], shadowsMask * shadowsMask)
	_shadows[1] = mix(1.0, shadowsColor[1], shadowsMask * shadowsMask)
	_shadows[2] = mix(1.0, shadowsColor[2], shadowsMask * shadowsMask)

	_highlights[0] = mix(1.0, highlightsColor[0], highlightsMask)
	_highlights[1] = mix(1.0, highlightsColor[1], highlightsMask)
	_highlights[2] = mix(1.0, highlightsColor[2], highlightsMask)

	_midtones[0] = mix(1.0, midtonesColor[0], midtonesMask)
	_midtones[1] = mix(1.0, midtonesColor[1], midtonesMask)
	_midtones[2] = mix(1.0, midtonesColor[2], midtonesMask)

	// Apply all color adjustments
	const result = _p_sub
	const temp = _p_sub_2

	temp[0] = color[0] * _global[0]
	temp[1] = color[1] * _global[1]
	temp[2] = color[2] * _global[2]
	mixVec3(result, color, temp, 0.8 + 0.5 * satMask)

	temp[0] = result[0] * _shadows[0]
	temp[1] = result[1] * _shadows[1]
	temp[2] = result[2] * _shadows[2]
	mixVec3(result, result, temp, 2.0)

	temp[0] = result[0] * _highlights[0]
	temp[1] = result[1] * _highlights[1]
	temp[2] = result[2] * _highlights[2]
	mixVec3(result, result, temp, 1.0 + 0.6 * satMask)

	result[0] = result[0] * _midtones[0]
	result[1] = result[1] * _midtones[1]
	result[2] = result[2] * _midtones[2]

	// Luminance adjustments
	const blackLevel = -shadowsColor[3] * 0.1
	const whiteLevel = highlightsColor[3] * 0.3
	const midsLevel = midtonesColor[3] * 0.33

	// Global luminance
	const globalLumPower = 1.0 - globalColor[3] * 0.5 * sqLuma
	result[0] = Math.pow(result[0], globalLumPower)
	result[1] = Math.pow(result[1], globalLumPower)
	result[2] = Math.pow(result[2], globalLumPower)

	// Mids luminance
	const midsLumPower = 1.0 - midsLevel * midtonesMask
	result[0] = Math.pow(result[0], midsLumPower)
	result[1] = Math.pow(result[1], midsLumPower)
	result[2] = Math.pow(result[2], midsLumPower)

	// Shadow + highlights luminance
	result[0] = Math.sqrt(Math.max(result[0], 0.0))
	result[1] = Math.sqrt(Math.max(result[1], 0.0))
	result[2] = Math.sqrt(Math.max(result[2], 0.0))

	result[0] = (result[0] - blackLevel) / (1.0 - whiteLevel - blackLevel)
	result[1] = (result[1] - blackLevel) / (1.0 - whiteLevel - blackLevel)
	result[2] = (result[2] - blackLevel) / (1.0 - whiteLevel - blackLevel)

	out[0] = result[0] * result[0]
	out[1] = result[1] * result[1]
	out[2] = result[2] * result[2]
	return out
}

function linearstep(edge0: number, edge1: number, x: number): number {
	return clamp((x - edge0) / (edge1 - edge0), 0, 1)
}

interface HSLPointColor {
	shiftHue: number
	shiftSat: number
	shiftLum: number
	range: number
	hueRange: vec4
	lumRange: vec4
	satRange: vec4
}

const MAX_HUE_SHIFT = 35.0

function calculateHueInfluence(source: number, hueRange: vec4): number {
	let adjustedSource = source

	// Make sure all numbers have the same sign
	if (hueRange[0] > hueRange[3]) {
		const delta = 1.01 - hueRange[0]
		hueRange = hueRange.map((v) => (v + delta) % 1.0) as vec4
		adjustedSource = (adjustedSource + delta) % 1.0
	}

	// Smooth interpolation
	const smoothStart = smoothstep(
		hueRange[0],
		Math.max(hueRange[1], hueRange[0] + 0.01),
		adjustedSource,
	)
	const smoothEnd =
		1.0 - smoothstep(hueRange[2], Math.max(hueRange[3], hueRange[2] + 0.01), adjustedSource)
	return smoothStart * smoothEnd
}

function blendInfluence(hueInfluence: number, satInfluence: number, lumInfluence: number): number {
	return hueInfluence * satInfluence * lumInfluence
}

function applyHsvDelta(out: vec3, color: vec3, delta: vec3): vec3 {
	const hsv = rgbToHsv(_p_hsv, color)

	const hue = (hsv[0] + (delta[0] * MAX_HUE_SHIFT) / 360.0 + 1.0) % 1.0
	const minSat = Math.min(0.15, hsv[1])
	const sat = clamp(hsv[1] + delta[1] * 0.4, minSat, 1.0)

	hsvToRgb(_p_sub_2, [hue, sat, hsv[2]])

	// Apply delta v as exposure instead of directly modifying v for more natural effect
	const maxMulti = 1.0 / Math.max(hsv[2], 0.01)

	// out = _p_sub_2.map((v) => v * Math.min(Math.pow(2, delta[2] * 1.1), maxMulti)) as vec3
	out[0] = _p_sub_2[0] * Math.min(Math.pow(2, delta[2] * 1.1), maxMulti)
	out[1] = _p_sub_2[1] * Math.min(Math.pow(2, delta[2] * 1.1), maxMulti)
	out[2] = _p_sub_2[2] * Math.min(Math.pow(2, delta[2] * 1.1), maxMulti)

	setHue(out, out, hue)

	return clipVec3(out, out, 0.0, 1.0)
}

const sourceHSV: vec3 = [0, 0, 0]
export function applyGlobalPointColorHsl(
	out: vec3,
	color: vec3,
	pointColors: HSLPointColor[],
): vec3 {
	// Convert to srgb because the passed-in range is in sRGB->HSV space
	const srgb = prophotoToSrgb(_p_sub, color)
	rgbToHsv(sourceHSV, srgb)

	vec3.copy(out, color)

	for (const pointColor of pointColors) {
		/* HSV distance and influence */
		const hueInfluence = calculateHueInfluence(sourceHSV[0], pointColor.hueRange)

		const lumInfluence =
			smoothstep(
				pointColor.lumRange[0],
				Math.max(pointColor.lumRange[1], pointColor.lumRange[0] + 0.01),
				sourceHSV[2],
			) *
			(1.0 -
				smoothstep(
					pointColor.lumRange[2],
					Math.max(pointColor.lumRange[3], pointColor.lumRange[2] + 0.01),
					sourceHSV[2],
				))

		const satInfluence =
			smoothstep(
				pointColor.satRange[0],
				Math.max(pointColor.satRange[1], pointColor.satRange[0] + 0.01),
				sourceHSV[1],
			) *
			(1.0 -
				smoothstep(
					pointColor.satRange[2],
					Math.max(pointColor.satRange[3], pointColor.satRange[2] + 0.01),
					sourceHSV[1],
				))

		const combinedInfluence = blendInfluence(hueInfluence, satInfluence, lumInfluence)

		const shiftedColor = applyHsvDelta(_p_sub, out, [
			pointColor.shiftHue,
			pointColor.shiftSat,
			pointColor.shiftLum,
		])

		// mix(result, shiftedColor, combinedInfluence)
		out[0] = out[0] * (1 - combinedInfluence) + shiftedColor[0] * combinedInfluence
		out[1] = out[1] * (1 - combinedInfluence) + shiftedColor[1] * combinedInfluence
		out[2] = out[2] * (1 - combinedInfluence) + shiftedColor[2] * combinedInfluence
	}

	return out
}

function isColorProfileMonochrome(params: Partial<Adjustments>): boolean {
	const colorProfile = params.colorProfile ?? ColorProfileName.Color
	return (
		colorProfile === ColorProfileName.Monochrome ||
		colorProfile === ColorProfileName.StandardMonochrome
	)
}

function getInverseCurve(points: vec2[], width: number): Float32Array {
	let pointsInv: vec2[] = points.map((p) => [p[1], p[0]])
	if (pointsInv[0]?.[0] !== 0) {
		pointsInv.unshift([0, 0])
	}

	// remove duplicate or smaller x_i compared to x_{i-1} to ensure the curve is monotonic
	pointsInv = pointsInv.filter((p, i, arr) => i === 0 || p[0] > (arr[i - 1]?.[0] ?? 0))

	if (pointsInv[pointsInv.length - 1]?.[0] !== 1) {
		pointsInv.push([1, 1])
	}

	return getCurve(pointsInv, width)
}

function applyAllCurves(color: vec3, uniforms: Partial<RenderData>): vec3 {
	if (
		uniforms.parametricCurveData == undefined &&
		uniforms.whiteCurveData == undefined &&
		uniforms.redCurveData == undefined &&
		uniforms.greenCurveData == undefined &&
		uniforms.blueCurveData == undefined
	) {
		return color
	}

	linearToGamma(color, color)

	const hue = rgbToHue(color)
	// apply parametric curve
	if (uniforms.parametricCurveData != undefined) {
		applyCurve(color, uniforms.parametricCurveData, "white")
	}
	// apply curves
	if (uniforms.whiteCurveData != undefined) {
		applyCurve(color, uniforms.whiteCurveData, "white")
	}
	setHue(color, color, hue)

	if (uniforms.redCurveData != undefined) {
		applyCurve(color, uniforms.redCurveData, "red")
	}
	if (uniforms.greenCurveData != undefined) {
		applyCurve(color, uniforms.greenCurveData, "green")
	}
	if (uniforms.blueCurveData != undefined) {
		applyCurve(color, uniforms.blueCurveData, "blue")
	}

	gammaToLinear(color, color)

	return color
}

export function renderInverseCurveProPhoto(
	color: Float32Array,
	adjustments: Partial<Adjustments>,
): Float32Array {
	const { curvesAll, curvesRed, curvesGreen, curvesBlue } = adjustments
	if (
		curvesAll == undefined &&
		curvesRed == undefined &&
		curvesGreen == undefined &&
		curvesBlue == undefined
	) {
		return color
	}

	const uniforms: Partial<RenderData> = {}
	if (curvesAll != undefined) {
		const points = curvesAll.map(normalizePoint)
		uniforms.whiteCurveData = getInverseCurve(points, 256)
	}
	if (curvesRed != undefined) {
		const points = curvesRed.map(normalizePoint)
		uniforms.redCurveData = getInverseCurve(points, 256)
	}
	if (curvesGreen != undefined) {
		const points = curvesGreen.map(normalizePoint)
		uniforms.greenCurveData = getInverseCurve(points, 256)
	}
	if (curvesBlue != undefined) {
		const points = curvesBlue.map(normalizePoint)
		uniforms.blueCurveData = getInverseCurve(points, 256)
	}

	return applyTransform(color, (out, pixel) => applyAllCurvesInverse(out, pixel, uniforms))
}

function applyAllCurvesInverse(out: vec3, color: vec3, uniforms: Partial<RenderData>): vec3 {
	let temp = _p_sub
	linearToGamma(temp, color)
	;[color, temp] = [temp, color]

	if (uniforms.blueCurveData != undefined) {
		applyCurve(color, uniforms.blueCurveData, "blue")
	}
	if (uniforms.greenCurveData != undefined) {
		applyCurve(color, uniforms.greenCurveData, "green")
	}
	if (uniforms.redCurveData != undefined) {
		applyCurve(color, uniforms.redCurveData, "red")
	}
	const hue = rgbToHue(color)

	// apply white curve
	if (uniforms.whiteCurveData != undefined) {
		applyCurve(color, uniforms.whiteCurveData, "white")
	}
	// apply parametric curve
	if (uniforms.parametricCurveData != undefined) {
		applyCurve(color, uniforms.parametricCurveData, "white")
	}
	setHue(temp, color, hue)
	;[color, temp] = [temp, color]

	gammaToLinear(out, color)

	return out
}

function sampleTextureBilinear(
	out: vec3, // The result will be written to this vector
	lookTableData: Float32Array,
	width: number,
	height: number,
	u: number,
	v: number,
) {
	// Convert normalized coordinates to pixel coordinates
	const x = u * (width - 1)
	const y = v * (height - 1)

	// Get integer coordinates for the four surrounding pixels
	const x0 = Math.floor(x)
	const y0 = Math.floor(y)
	const x1 = Math.min(x0 + 1, width - 1)
	const y1 = Math.min(y0 + 1, height - 1)

	// Calculate interpolation weights
	const fx = x - x0
	const fy = y - y0

	const p00_idx = (y0 * width + x0) * 3
	const p01_idx = (y0 * width + x1) * 3
	const p10_idx = (y1 * width + x0) * 3
	const p11_idx = (y1 * width + x1) * 3

	// --- No new arrays are created below this line ---

	// Bilinear interpolation for the 'red' channel
	const c0_r = mix(lookTableData[p00_idx] ?? 0, lookTableData[p01_idx] ?? 0, fx)
	const c1_r = mix(lookTableData[p10_idx] ?? 0, lookTableData[p11_idx] ?? 0, fx)
	out[0] = mix(c0_r, c1_r, fy)

	// Bilinear interpolation for the 'green' channel
	const c0_g = mix(lookTableData[p00_idx + 1] ?? 0, lookTableData[p01_idx + 1] ?? 0, fx)
	const c1_g = mix(lookTableData[p10_idx + 1] ?? 0, lookTableData[p11_idx + 1] ?? 0, fx)
	out[1] = mix(c0_g, c1_g, fy)

	// Bilinear interpolation for the 'blue' channel
	const c0_b = mix(lookTableData[p00_idx + 2] ?? 0, lookTableData[p01_idx + 2] ?? 0, fx)
	const c1_b = mix(lookTableData[p10_idx + 2] ?? 0, lookTableData[p11_idx + 2] ?? 0, fx)
	out[2] = mix(c0_b, c1_b, fy)
}

// Pre-allocate temporary vectors in a higher scope to be reused
const _applyLookTable_temp_col1: vec3 = [0, 0, 0]
const _applyLookTable_temp_col2: vec3 = [0, 0, 0]

function applyLookTable(out: vec3, color: vec3, dims: vec3, lookTableData: Float32Array): vec3 {
	const hueDivs = dims[0]
	const satDivs = dims[1]
	const valDivs = dims[2]

	const invSatDivs = 1.0 / satDivs
	const invValDivs = 1.0 / valDivs

	const hsv = rgbToHsv(_p_hsv, color)

	// Each column is a sat division
	// Each row is a hue division (hue is indexed using "wrap-around" math)
	// Each slice is a val division
	const x = (hsv[1] * (satDivs - 1.0) + 0.5) * invSatDivs
	const y = (hsv[0] * hueDivs + 0.5) / hueDivs
	const z = hsv[2] * (valDivs - 1.0)

	const z_floor = Math.floor(z)
	const zf = (z_floor + y) * invValDivs
	const zc = (Math.ceil(z) + y) * invValDivs

	// Calculate texture dimensions
	const width = Math.round(satDivs)
	const height = Math.round(hueDivs * valDivs)

	// Sample with bilinear interpolation (like GLSL texture())
	sampleTextureBilinear(_applyLookTable_temp_col1, lookTableData, width, height, x, zf)
	sampleTextureBilinear(_applyLookTable_temp_col2, lookTableData, width, height, x, zc)

	// Interpolate between the two samples
	const fractZ = z - z_floor
	const factors_r = mix(_applyLookTable_temp_col1[0], _applyLookTable_temp_col2[0], fractZ)
	const factors_g = mix(_applyLookTable_temp_col1[1], _applyLookTable_temp_col2[1], fractZ)
	const factors_b = mix(_applyLookTable_temp_col1[2], _applyLookTable_temp_col2[2], fractZ)

	// Apply the factors to HSV
	hsv[0] = (hsv[0] + factors_r) % 1.0 // fract(hsv.x + factors.x)
	hsv[1] = hsv[1] * factors_g // No clip
	hsv[2] = hsv[2] * factors_b // No clip

	return hsvToRgb(out, hsv)
}

// --- RGB user LUT support ---

const _rgb_lut_temp: vec3 = [0, 0, 0]
const _rgb_lut_temp2: vec3 = [0, 0, 0]
const _rgb_lut_col1: vec3 = [0, 0, 0]
const _rgb_lut_col2: vec3 = [0, 0, 0]

function applyLookTableRGB(
	out: vec3,
	color: vec3,
	dims: vec3,
	lookTableData: Float32Array,
	strength: number,
	gammaType: number,
	primariesType: number,
): vec3 {
	const originalR = color[0]
	const originalG = color[1]
	const originalB = color[2]

	lutPrimariesEncode(_rgb_lut_temp, color, primariesType)

	_rgb_lut_temp[0] = clamp(_rgb_lut_temp[0], 0, 1)
	_rgb_lut_temp[1] = clamp(_rgb_lut_temp[1], 0, 1)
	_rgb_lut_temp[2] = clamp(_rgb_lut_temp[2], 0, 1)

	lutGammaEncode(_rgb_lut_temp, _rgb_lut_temp, gammaType)

	// 3D lookup: dims.y is the cube edge size
	const lutSize = dims[1]
	const sizeIndex = lutSize - 1.0

	const b = _rgb_lut_temp[2]
	const r = _rgb_lut_temp[0]
	const g = _rgb_lut_temp[1]

	const texX_base = (b * sizeIndex + 0.5) / lutSize
	const texY = (r * sizeIndex + 0.5) / lutSize
	const z = g * sizeIndex

	// RGB LUT is packed as width = lutSize * lutSize, height = lutSize
	const width = Math.round(lutSize * lutSize)
	const height = Math.round(lutSize)

	const zFloor = Math.floor(z)
	const zf = (zFloor + texX_base) / lutSize
	const zc = (Math.min(zFloor + 1, sizeIndex) + texX_base) / lutSize

	sampleTextureBilinear(_rgb_lut_col1, lookTableData, width, height, zf, texY)
	sampleTextureBilinear(_rgb_lut_col2, lookTableData, width, height, zc, texY)

	const fractZ = z - zFloor
	_rgb_lut_temp2[0] = mix(_rgb_lut_col1[0], _rgb_lut_col2[0], fractZ)
	_rgb_lut_temp2[1] = mix(_rgb_lut_col1[1], _rgb_lut_col2[1], fractZ)
	_rgb_lut_temp2[2] = mix(_rgb_lut_col1[2], _rgb_lut_col2[2], fractZ)

	lutGammaDecode(_rgb_lut_temp2, _rgb_lut_temp2, gammaType)
	lutPrimariesDecode(_rgb_lut_temp2, _rgb_lut_temp2, primariesType)

	out[0] = mix(originalR, _rgb_lut_temp2[0], strength)
	out[1] = mix(originalG, _rgb_lut_temp2[1], strength)
	out[2] = mix(originalB, _rgb_lut_temp2[2], strength)
	return out
}
